import { useSuspenseQuery } from '@tanstack/react-query';
import type { GoToTest } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import { isAxiosError } from 'axios';

interface GetDetailsParams {
  id?: string;
}

export const getGoToTestDetails = async (
  { id, ...params }: GetDetailsParams,
  signal?: AbortSignal,
): Promise<GoToTest> => {
  const res = await apiClient.get<GoToTest>(
    `${queryKeys.goToTest}/${id}`,
    {
      signal,
      params,
    },
  );

  if (isAxiosError(res)) {
    return handleAxiosError(res, 'Failed to get "Go to test".');
  }

  return res.data;
};

interface UseOptions<TData> {
  config?: QueryConfig<typeof getGoToTestDetails, TData>;
  params: GetDetailsParams;
}

export const useGoToTestDetails = <TData = GoToTest>(
  {
    config,
    params,
  }: UseOptions<TData>) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getGoToTestDetails(params, signal),
    queryKey: [queryKeys.goToTestDetails, params.id],
  });
};
